import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import Input from "../../components/Forms/Input";
import Button from "../../components/Button";
import PaddleCheckout from "../../components/PaddleCheckout/PaddleCheckout";

import { capture } from "../../analytics";

import styles from "./CheckOut.module.css";

const fields = [
  { name: "description", placeholder: "Description" },
  { name: "first_line", placeholder: "First Line" },
  { name: "city", placeholder: "City" },
  { name: "postal_code", placeholder: "Postal Code" },
  { name: "region", placeholder: "Region" },
  { name: "country_code", placeholder: "Country Code" },
];

const Payment = ({ orderId, order }) => {
  const { status } = useSelector((state) => state.payment);

  const [values, setValues] = useState({
    description: "",
    first_line: "",
    city: "",
    postal_code: "",
    region: "",
    country_code: "",
  });
  const [errors, setErrors] = useState({});
  const [isChecked, setIsChecked] = useState(false);
  const [labelClass, setLabelClass] = useState(styles.checkboxLabel);
  const [showPaddleCheckout, setShowPaddleCheckout] = useState(false);
  const termsAcceptedTrackedRef = useRef(false);

  // Check if user is returning from payment completion
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentCompleted = urlParams.get('payment_completed');
    const transactionId = urlParams.get('transaction_id');
    
    if (paymentCompleted === 'true' && transactionId) {
      console.log('Payment completed, redirecting to profile');
      // Redirect to profile page
      window.location.href = '/profile';
    }
  }, []);

  // Validation
  const validateFields = () => {
    const newErrors = {};
    // Temporarily disable validation to allow payment to proceed
    // fields.forEach(({ name, placeholder }) => {
    //   if (!values[name]) {
    //     newErrors[name] = `${placeholder} is required`;
    //   }
    // });
    return newErrors;
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
    if (e.target.checked) {
      setLabelClass(styles.checkboxLabel);
      if (!termsAcceptedTrackedRef.current) {
        termsAcceptedTrackedRef.current = true;
        capture("checkout_terms_accepted", {
          order_id: orderId,
          order_items_count: order?.items?.length || 0,
          order_total_price: order?.total_price,
          order_final_amount: order?.final_amount,
        });
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isChecked) {
      setLabelClass(styles.notActive);
      capture("checkout_validation_failed", {
        order_id: orderId,
        reason: "terms_not_accepted",
      });
      return;
    }

    const newErrors = validateFields();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      capture("checkout_validation_failed", {
        order_id: orderId,
        reason: "field_validation",
        field_count: Object.keys(newErrors).length,
      });
      return;
    }

    // Show Paddle checkout
    capture("checkout_payment_started", {
      order_id: orderId,
      order_items_count: order?.items?.length || 0,
      order_total_price: order?.total_price,
      order_final_amount: order?.final_amount,
      order_discount_amount: order?.discount_amount,
    });
    setShowPaddleCheckout(true);
  };

  // Handle back to form
  const handleBackToForm = () => {
    setShowPaddleCheckout(false);
  };

  if (showPaddleCheckout) {
    return (
      <div className={styles.formcontainer}>
        <div className={styles.paddleCheckoutWrapper}>
          <button 
            className={styles.backButton}
            onClick={handleBackToForm}
          >
            ← Back to form
          </button>
          <PaddleCheckout 
            orderId={orderId} 
            orderData={{ address: values }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formcontainer}>
      {status === "failed" && (
        <div className="error-message">Something went wrong</div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className={`form-group ${styles.forms} ${styles.socialtoggle}`} style={{ gap: '15px' }}>
          {/* Render form fields */}
          {fields.map(({ name, placeholder }) => (
            <Input
              key={name}
              name={name}
              placeholder={placeholder}
              value={values[name]}
              onChange={handleChange}
              errors={errors[name] || ""}
            />
          ))}

          {/* Terms & Conditions block */}
          <div>
            <p className={styles.agreement_text}>
              By proceeding with your purchase, you acknowledge that you have read, understood, and agree to the following:
            </p>
            <label className={labelClass}>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={handleCheckboxChange}
                className={styles.checkboxInput}
                style={{ height: "20px", width: "20px" }}
              />
              I have read and agree to the{" "}
              <a
                href="/terms-of-use"
                style={{ color: "blue", textDecoration: "underline" }}
                target="_blank"
                rel="noreferrer"
              >
                Terms of use
              </a> &{" "}
              <a
                href="/privacy-policy"
                style={{ color: "blue", textDecoration: "underline" }}
                target="_blank"
                rel="noreferrer"
              >
                Privacy policy
              </a>
            </label>
          </div>

          {/* Pay button */}
          <Button
            variant={status === "loading" ? "grey" : "blue"}
            label={status === "loading" ? "Processing..." : "Proceed to Payment"}
            iconType="arrowRight"
            type="submit"
          />
        </div>
      </form>
    </div>
  );
};

export default Payment;
